<template>
  <v-menu
    v-model="profileMenu"
    :close-on-content-click="false"
    :nudge-width="200"
    offset-x
  >
    <template #activator="{ on, attrs }">
      <v-list-item
        v-if="$vuetify.breakpoint.mdAndDown"
        class="py-1"
        v-bind="attrs"
        v-on="on"
      >
        <v-btn
          color="indigo"
          dark
          icon
          :ripple="false"
        >
          <v-avatar size="40">
            <v-img :src="userAvatar" :alt="userFullName" />
          </v-avatar>
        </v-btn>
        <span class="text--secondary ml-3">
          {{ userFullName }}
          <br>
          {{ companyName }}
        </span>
      </v-list-item>
      <v-btn
        v-else
        color="indigo"
        dark
        icon
        :ripple="false"
        class="ml-2"
        v-bind="attrs"
        v-on="on"
      >
        <v-avatar size="40">
          <v-img :src="userAvatar" :alt="userFullName" />
        </v-avatar>
      </v-btn>
    </template>
    <!-- BETA-CLEANUP: caps the width so the beta promo's text wraps instead of stretching the menu -->
    <v-card max-width="420">
      <v-list>
        <v-list-item>
          <v-list-item-avatar>
            <img :src="userAvatar" alt="John">
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title> {{ userFullName }}</v-list-item-title>
            <v-list-item-subtitle>{{ companyName }}</v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn icon @click="profileMenu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <!-- BETA-CLEANUP -->
      <template v-if="isBetaAvailable">
        <v-divider />
        <ProfileMenuBetaPromo />
      </template>
      <v-divider />
      <v-list class="pb-0 pt-0">
        <v-list-item :to="getAccountRoute({ name: 'user-profile' })">
          <v-list-item-icon>
            <v-icon>mdi-account-circle</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Profile</v-list-item-title>
        </v-list-item>
        <template v-if="$vuetify.breakpoint.lgAndUp && !isClient">
          <v-divider />
          <v-list-item :to="getAccountRoute({ name: 'administration-root-page' })">
            <v-list-item-icon>
              <v-icon>mdi-cog</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text--secondary">Account Settings</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
      <template v-if="userAccounts && userAccounts.length > 1">
        <v-divider />
        <v-list-item to="/my-accounts">
          <v-list-item-icon>
            <v-icon>mdi-vector-combine</v-icon>
          </v-list-item-icon>
          <v-list-item-content v-if="activeAccount">
            <v-list-item-title>{{ activeAccount.name }}</v-list-item-title>
            <v-list-item-subtitle>Click here to switch accounts</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-if="$vuetify.breakpoint.lgAndUp">
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text class="cancel" @click="logoutHandler">
            <v-icon small class="mr-2">mdi-logout</v-icon> Logout
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { mapActions, mapMutations, mapState } from 'vuex';
import mixins from 'vue-typed-mixins';

import { IUserAccount } from '@/bundles/User/interfaces';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { RootActions } from '@/store/types/action-types';
import { isBetaAvailable } from '@/bundles/App/helpers/betaChannel'; // BETA-CLEANUP

import ProfileMenuBetaPromo from '@/bundles/Panels/components/ProfileMenuBetaPromo.vue'; // BETA-CLEANUP

interface IData {
  profileMenu: boolean;
  isBetaAvailable: boolean; // BETA-CLEANUP
}

export default mixins(AccountLayoutMixin).extend({
  name: 'ProfileMenu',

  components: { ProfileMenuBetaPromo },

  data: (): IData => ({
    profileMenu: false,
    isBetaAvailable,
  }),

  computed: {
    ...mapState({
      user: (state: any) => state.user,
      userFullName: (state: any) => state.userName,
      companyName: (state: any) => state.company,
    }),
    userAvatar: function (): string {
      return this.user?.profile?.img || this.contactPlaceholder;
    },
    userAccounts (): IUserAccount[] {
      return this.user?.account;
    },
    activeAccount (): IUserAccount {
      return this.user?.active_account;
    },
  },

  methods: {
    ...mapMutations('Notifications', {
      addNotification: 'ADD_NOTIFICATION'
    }),
    ...mapActions({
      logout: RootActions.LOG_OUT
    }),
    logoutHandler: async function () {
      try {
        await this.logout();
      } catch (error) {
        const notification = { ...error };
        this.addNotification(notification);
      }
    },
  }
})
</script>
